import { createSlice } from 'utils/@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { baseQuery, endpoints } from 'utils/api/endpoints';
import {
  OfflinePaymentConfig,
  OfflinePaymentMethodResponse,
  OfflinePaymentMethodUpdateRequest,
  OfflinePaymentMethodAddRequest,
} from '../../../types/offlinePayment';

// API endpoints
export const offlinePaymentApi = createApi({
  reducerPath: 'offlinePaymentMethodsApi',
  baseQuery,
  tagTypes: ['OfflinePaymentMethods'],
  endpoints: builder => ({
    // Get all offline payment methods
    getAllOfflinePaymentMethods: builder.query<
      OfflinePaymentConfig['paymentConfig'],
      void
    >({
      query: () => ({
        url: endpoints.OFFLINE_PAYMENT_METHODS,
        method: 'GET',
      }),
      providesTags: ['OfflinePaymentMethods'],
      transformResponse: (response: OfflinePaymentMethodResponse) => {
        return response.data || { indian: {}, worldwide: {} };
      },
    }),

    // Get payment methods by category
    getOfflinePaymentMethodsByCategory: builder.query<
      Record<string, any>,
      { category: 'indian' | 'worldwide' }
    >({
      query: ({ category }) => ({
        url: `${endpoints.OFFLINE_PAYMENT_METHODS_BY_CATEGORY}/${category}`,
        method: 'GET',
      }),
      providesTags: ['OfflinePaymentMethods'],
    }),

    // Get specific payment method
    getOfflinePaymentMethod: builder.query<
      OfflinePaymentMethod,
      { category: 'indian' | 'worldwide'; methodType: string }
    >({
      query: ({ category, methodType }) => ({
        url: `${endpoints.OFFLINE_PAYMENT_METHODS_SPECIFIC}/${category}/${methodType}`,
        method: 'GET',
      }),
      providesTags: ['OfflinePaymentMethods'],
    }),

    // Update specific payment method
    updateOfflinePaymentMethod: builder.mutation<
      OfflinePaymentMethodResponse,
      OfflinePaymentMethodUpdateRequest
    >({
      query: ({ category, methodType, title, icon, badge, details }) => ({
        url: `${endpoints.OFFLINE_PAYMENT_METHODS_UPDATE}/${category}/${methodType}`,
        method: 'PUT',
        body: { title, icon, badge, details },
      }),
      invalidatesTags: ['OfflinePaymentMethods'],
    }),

    // Add new payment method
    addOfflinePaymentMethod: builder.mutation<
      OfflinePaymentMethodResponse,
      OfflinePaymentMethodAddRequest
    >({
      query: body => ({
        url: endpoints.OFFLINE_PAYMENT_METHODS_CREATE,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['OfflinePaymentMethods'],
    }),

    // Delete payment method
    deleteOfflinePaymentMethod: builder.mutation<
      OfflinePaymentMethodResponse,
      { category: 'indian' | 'worldwide'; methodType: string }
    >({
      query: ({ category, methodType }) => ({
        url: `${endpoints.OFFLINE_PAYMENT_METHODS_DELETE}/${category}/${methodType}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['OfflinePaymentMethods'],
    }),

    // Toggle payment method status
    toggleOfflinePaymentMethodStatus: builder.mutation<
      OfflinePaymentMethodResponse,
      { category: 'indian' | 'worldwide'; methodType: string; is_active: boolean }
    >({
      query: ({ category, methodType, is_active }) => ({
        url: `${endpoints.OFFLINE_PAYMENT_METHODS_TOGGLE_STATUS}/${category}/${methodType}/toggle-status`,
        method: 'PATCH',
        body: { is_active },
      }),
      invalidatesTags: ['OfflinePaymentMethods'],
    }),
  }),
});

// Slice for offline payment methods
const offlinePaymentSlice = createSlice({
  name: 'offlinePayment',
  initialState: {
    selectedCategory: 'indian' as 'indian' | 'worldwide',
    selectedMethod: null as string | null,
    isEditing: false,
    isViewing: false,
  },
  reducers: {
    setSelectedCategory: (state, action) => {
      state.selectedCategory = action.payload;
    },
    setSelectedMethod: (state, action) => {
      state.selectedMethod = action.payload;
    },
    setIsEditing: (state, action) => {
      state.isEditing = action.payload;
    },
    setIsViewing: (state, action) => {
      state.isViewing = action.payload;
    },
    resetSelection: state => {
      state.selectedMethod = null;
      state.isEditing = false;
      state.isViewing = false;
    },
  },
});

export const {
  setSelectedCategory,
  setSelectedMethod,
  setIsEditing,
  setIsViewing,
  resetSelection,
} = offlinePaymentSlice.actions;

// Export hooks for usage in functional components
export const {
  useGetAllOfflinePaymentMethodsQuery,
  useGetOfflinePaymentMethodsByCategoryQuery,
  useGetOfflinePaymentMethodQuery,
  useUpdateOfflinePaymentMethodMutation,
  useAddOfflinePaymentMethodMutation,
  useDeleteOfflinePaymentMethodMutation,
  useToggleOfflinePaymentMethodStatusMutation,
} = offlinePaymentApi;

// Export the slice reducer
export default offlinePaymentSlice.reducer;

// Selectors
export const selectOfflinePaymentState = (state: any) => state.offlinePayment;
export const selectSelectedCategory = (state: any) =>
  state.offlinePayment.selectedCategory;
export const selectSelectedMethod = (state: any) =>
  state.offlinePayment.selectedMethod;
export const selectIsEditing = (state: any) => state.offlinePayment.isEditing;
export const selectIsViewing = (state: any) => state.offlinePayment.isViewing;

// Hook to use the slice
export const useOfflinePaymentSlice = () => {
  useInjectReducer({
    key: offlinePaymentSlice.name,
    reducer: offlinePaymentSlice.reducer,
  });
  useInjectReducer({
    key: offlinePaymentApi.reducerPath,
    reducer: offlinePaymentApi.reducer,
  });
  return {
    actions: offlinePaymentSlice.actions,
    useGetAllOfflinePaymentMethodsQuery,
    useGetOfflinePaymentMethodsByCategoryQuery,
    useGetOfflinePaymentMethodQuery,
    useUpdateOfflinePaymentMethodMutation,
    useAddOfflinePaymentMethodMutation,
    useDeleteOfflinePaymentMethodMutation,
    useToggleOfflinePaymentMethodStatusMutation,
  };
};
